import type { ClassId } from '../../types/progression'

/**
 * Imágenes de clases, NPC y monstruos, asociadas al ID exacto de su entidad.
 *
 * - Una imagen de clase corresponde solo a la clase inicial; sus evoluciones necesitan su propia entrada.
 * - `origin: 'staff'` = "Imagen proporcionada por el staff". Esto identifica a la entidad; no confirma drops,
 *   coordenadas, estadísticas ni recetas.
 * - `matchedBy` explica cómo se asoció cada archivo (las subidas llegaron como "image.png", sin el nombre
 *   clase_/npc_/monstruo_), para que el staff pueda revisar la correspondencia.
 */
export type ImageOrigin = 'staff' | 'paquete'

export type EntityImage = {
  src: string
  /** Foto recortada de una lámina: rectángulo [x, y, ancho, alto] relativo a una lámina de sheetW × sheetH. */
  crop?: { sheetW: number; sheetH: number; rect: [number, number, number, number]; focus?: { fx: number; fy: number; zoom: number } }
  /** Nombre impreso en el paquete (procedencia). */
  sourceName?: string
  origin: ImageOrigin
  addedOn: string
  /** Cómo se asoció el archivo a la entidad. */
  matchedBy: string
  /** Asociación que el staff debe confirmar antes de darla por segura. */
  needsConfirmation?: boolean
  /** Foto compartida con otros IDs: referencia de familia (misma apariencia), no la misma entidad. */
  familyReference?: { sharedWith: number[] }
  /** Paquete del que procede (para el apartado de procedencia). */
  packName?: string
}

export type ImageEntryKind = 'clase' | 'npc' | 'monstruo'

export type ImageEntry = {
  key: string
  label: string
  kind: ImageEntryKind
  classId?: ClassId
  /** ID exacto en 06_monsters (NPC y monstruos). */
  monsterId?: number
  /** Solo si el ID no está fijado: se resuelve por nombre exacto y únicamente con una sola coincidencia. */
  monsterName?: string
  image?: EntityImage
  pending?: string
}

const DATE = '10/10/2026'
const staff = (src: string, matchedBy: string, needsConfirmation = false): EntityImage => ({ src, origin: 'staff', addedOn: DATE, matchedBy, needsConfirmation })
const CDN = 'https://cdn.magicpatterns.com/uploads'
const ORDER = 'Asociada por el orden de envío (llegó como "image.png", sin nombre de entidad) y por su aspecto'
const LABEL = 'El nombre aparece escrito sobre el personaje en la captura'
const FILE = 'Asociada por el nombre del archivo'
const STAFF_SWAP_2 = 'Corregida por el staff: Golden Satyros y Golden Iron Knight intercambiados'
const STAFF_SWAP = 'Corregida por el staff: Golden Derkon y Golden Lizard King intercambiados'
const GOLD_ORDER = 'Invasión de Dorados: asociada por el orden de envío y por su aspecto'

/** Dorado por su ID exacto en 06_monsters. `unsure` = el aspecto no basta; el staff debe confirmarlo. */
function golden(id: number, label: string, path: string, look: string, unsure = false, how = GOLD_ORDER): ImageEntry[] {
  return [{ key: `monstruo:${id}`, label, kind: 'monstruo', monsterId: id, image: staff(`${CDN}/${path}`, `${how} (${look})`, unsure) }]
}

export const entityImages: ImageEntry[] = [
  { key: 'clase:dk', label: 'Dark Knight', kind: 'clase', classId: 'dk', image: staff(`${CDN}/gaBrfPnk1L7URyjrXSM7CQ/image.png`, ORDER) },
  { key: 'clase:dw', label: 'Dark Wizard', kind: 'clase', classId: 'dw', image: staff(`${CDN}/ax84a7DyPr3veMQFyLEy7u/image.png`, ORDER) },
  { key: 'clase:elf', label: 'Fairy Elf', kind: 'clase', classId: 'elf', image: staff(`${CDN}/i39j9RAhcwDGGymp3NFTRS/image.png`, ORDER) },
  { key: 'clase:sum', label: 'Summoner', kind: 'clase', classId: 'sum', image: staff(`${CDN}/qfDwtaX1UsuNWhKajrWuRR/image.png`, ORDER) },
  { key: 'clase:mg', label: 'Magic Gladiator', kind: 'clase', classId: 'mg', image: staff(`${CDN}/5RwLn6PAkky5YJQNcPtneX/image.png`, ORDER) },
  { key: 'clase:dl', label: 'Dark Lord', kind: 'clase', classId: 'dl', image: staff(`${CDN}/getDimKHRgAcd4v4te3uwQ/image.png`, ORDER) },
  { key: 'clase:rf', label: 'Rage Fighter', kind: 'clase', classId: 'rf', image: staff(`${CDN}/47ApDiNqN7ycDUXSeUWRy8/image.png`, ORDER) },
  { key: 'monstruo:235', label: 'Sebina the Priest', kind: 'npc', monsterId: 235, image: staff(`${CDN}/egoa1woT4kUUkuUTw287Gq/image.png`, LABEL) },
  { key: 'monstruo:229', label: 'Marlon', kind: 'npc', monsterId: 229, image: staff(`${CDN}/roRaYMZ2vWknrCTxZVik6E/image.png`, LABEL) },
  { key: 'monstruo:238', label: 'Chaos Goblin', kind: 'npc', monsterId: 238, image: staff(`${CDN}/haQXk78phhw5G3hSN6aE6y/image.png`, LABEL) },
  { key: 'monstruo:406', label: 'Apostle Devin', kind: 'npc', monsterId: 406, image: staff(`${CDN}/r8pj5tGyBkHA4YWwfC2wBC/image.png`, `${ORDER}; la captura no muestra el nombre`, true) },
  { key: 'monstruo:407', label: 'Werewolf Quarel', kind: 'npc', monsterId: 407, image: staff(`${CDN}/mfoD2a3bjLmYUpPhimRsyi/image.png`, `${ORDER}; la captura no muestra el nombre`, true) },
  { key: 'monstruo:408', label: 'Gatekeeper', kind: 'npc', monsterId: 408, image: staff(`${CDN}/coawYWAxLawv2ruqPXM2MC/image.png`, `${ORDER}; puerta con rostro, como el Gatekeeper`) },
  { key: 'monstruo:226', label: 'Trainer', kind: 'npc', monsterId: 226, image: staff(`${CDN}/qgDdKd5yFZPqQofCQB52rv/image.png`, 'Confirmada por el staff: es el Trainer (el mismo NPC que "Pet Trainer")') },
  { key: 'monstruo:805', label: 'Robot', kind: 'npc', monsterId: 805, image: staff(`${CDN}/ukCrqGqoiXVKFsFZYyLBJ2/image.png`, 'Confirmada por el staff: es el Robot vendedor de sets; el nombre aparece sobre el NPC en la captura') },
  { key: 'monstruo:0', label: 'Bull Fighter', kind: 'monstruo', monsterId: 0, image: staff(`${CDN}/ix9YqyBpQB1HuEATpkbPkj/image.png`, 'Corregida por el staff: reemplaza la foto del paquete para Bull Fighter (ID 0)') },
  { key: 'monstruo:42', label: 'Red Dragon', kind: 'monstruo', monsterId: 42, image: staff(`${CDN}/jfCUkdtkrTmKZhsnLzpYxD/image.png`, ORDER) },
  { key: 'nombre:white-wizard', label: 'White Wizard', kind: 'monstruo', monsterName: 'White Wizard', image: staff(`${CDN}/ipPu2ABaGiysedUUVHiiT2/image.png`, ORDER) },
  { key: 'nombre:death-king', label: 'Death King', kind: 'monstruo', monsterName: 'Death King', image: staff(`${CDN}/vHwLvbeqi4y8GDfAodkGzT/image.png`, LABEL) },
  // ── Invasión de Dorados (invasion:2) · subidas del staff ──
  ...golden(43, 'Golden Budge Dragon', '3DbWm1zBoRjCENqLb8NHqd/image.png', 'dragón pequeño dorado'),
  ...golden(78, 'Golden Goblin', 'hZDbor3inR9t7EV4BKjVTE/image.png', 'goblin delgado con lanza', true),
  ...golden(502, 'Golden Rabbit', '2D11UALDVkR6wn3uvMFtVP/golden_rabbit.webp', 'archivo "golden_rabbit"', false, FILE),
  ...golden(493, 'Golden Dark Knight', 'jiehiL4veHFAuzt9QMdmJx/golden_knight.webp', 'archivo "golden_knight"', false, FILE),
  ...golden(53, 'Golden Titan', 'aKHzw7oARPSxEiNjXsHXMU/image.png', 'titán con hombreras grandes'),
  ...golden(54, 'Golden Soldier', 'syw82N6ZgE67sfcEUAT6o3/image.png', 'soldado con hacha brillante'),
  ...golden(79, 'Golden Derkon', '313hHEATU9PwnFuWPQ6kkn/image.png', 'dragón con alas grandes', false, STAFF_SWAP),
  ...golden(494, 'Golden Devil', 'aKn5ZpzoAJ61omc7Vp53E2/golden_devil.webp', 'archivo "golden_devil"', false, FILE),
  ...golden(81, 'Golden Vepar', 'tdPxTQg7aeZyJYP35oNepi/golden_vepar.webp', 'archivo "golden_vepar"', false, FILE),
  ...golden(80, 'Golden Lizard King', 'iVXNpKehAp6FD9HMYBw4EK/image.png', 'guerrero con lanza azul', false, STAFF_SWAP),
  ...golden(82, 'Golden Tantalos', 'tKpthVXVuU54siknNWovxW/image.png', 'gigante acorazado con espada enorme'),
  ...golden(83, 'Golden Wheel', 'oj2HEkvh49c5awQfgNVY3M/image.png', 'jinete sobre una rueda con púas'),
  ...golden(500, 'Golden Napin', 'sJaAuoeX5MDDychmE64LmG/image.png', 'gólem con energía azul', true),
  ...golden(496, 'Golden Crust', 'iGwfD44LzGTt3FhpKzRYTf/image.png', 'criatura alada', true),
  ...golden(499, 'Golden Iron Knight', 'dYL2GVfwDAS4doNkrenQHk/image.png', 'guerrero con cuernos', false, STAFF_SWAP_2),
  ...golden(498, 'Golden Twin Tail', '71RFK1fBpAtD1BkvgZ8NFM/image.png', 'criatura delgada con lanza', true),
  ...golden(495, 'Golden Stone Golem', 'rmKtGCvx8GHyn7vJbp13oe/image.png', 'gólem de roca'),
  ...golden(497, 'Golden Satyros', '39yCCy9RgSsJNu83GGSwoe/image.png', 'caballero con púas de hielo', false, STAFF_SWAP_2),
  ...golden(501, 'Great Golden Dragon', '7JbZe4RZ31axiWAb84uaFi/image.png', 'dragón dorado grande'),
]

/** Imágenes recibidas sin identificación suficiente: no se muestran a los jugadores. */
export const unassignedImages: { src: string; description: string; question: string }[] = []
